import React, { useState, useEffect, useCallback } from 'react'
import { Alert, Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useAssetTypesState } from '../../../stores/Configuration/AssetTypesProvider'
import { ButtonWithLoadingState } from '../../Buttons'
import LanguageForm from '../../Forms/LanguageForm'
import LanguageNameInput from '../../Forms/LanguageNameInput'
import OverlayContainer from '../../Page/logix2020/OverlayContainer'
import OverlayRight from '../../Page/logix2020/OverlayRight'
import AssetTypeDefaultForm from './SystemTypes/AssetTypeDefaultForm'
import ArraySelect from '../../Select/ArraySelect'
import useMountEffect from '../../../utils/hooks/useMountEffect'
import { setValueByKey } from '../../../utils/utils'
import { getMetaProperty } from '../../../utils/customerPrototypes'
import { isEmptyGuid } from '../../../utils/emptyGuid'

const StyledDiv = styled.div`
  max-width: 600px;
  .alert {
    margin-top: 1.5rem;
  }
`
const AssetTypeGeneralForm = ({ assetTypeId }) => {
  const { t } = useTranslation()
  const {
    assetTypes,
    getAssetTypes,
    assetType,
    setAssetType,
    loadingStates,
    successStates,
    errorStates,
    patchAssetType,
    postAssetType,
  } = useAssetTypesState()
  const [overlayNameIsVisible, setOverlayNameIsVisible] = useState(false)
  const [overlayDescriptionIsVisible, setOverlayDescriptionIsVisible] =
    useState(false)
  const [systemTypeId, setSystemTypeId] = useState()
  const [systemTypes, setSystemTypes] = useState([])
  const [formErrors, setFormErrors] = useState({})
  const [hideSelectSystemType, setHideSelectSystemType] = useState(false)

  useMountEffect(() => {
    if (assetTypeId) {
      setHideSelectSystemType(true)
    } else {
      getAssetTypes()
    }
  })

  useEffect(() => {
    let sysTypes = assetTypes.filter((item) => item.isSystemType)
    setSystemTypes(sysTypes)
    if (!assetTypeId && sysTypes.length === 1) {
      setSelectedSystemType(sysTypes[0].id)
      setHideSelectSystemType(true)
    }
  }, [assetTypes, assetTypeId])
  const handleSubmit = () => {
    assetTypeId ? patchAssetType(assetTypeId) : postAssetType()
  }

  const handleCloseOverlay = () => {
    setOverlayNameIsVisible(false)
    setOverlayDescriptionIsVisible(false)
  }

  const updateTranslations = (name) => (translations) => {
    setAssetType((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        values: translations,
      },
    }))
    resetErrorByFormKey(name)
  }
  const updateConfiguration = (configuration) => {
    setAssetType((prev) => ({
      ...prev,
      fieldConfiguration: configuration,
    }))
  }
  const updateInputFields = (e) => {
    const { target } = e
    let key = target.name
    let value = null
    switch (key) {
      default:
        if (target.type === 'checkbox') {
          value = target.checked
        } else {
          value = target.value
        }
        setAssetType((prev) => setValueByKey(key, value, prev))
        break
    }
    resetErrorByFormKey(key)
  }
  const setSelectedSystemType = (id) => {
    setSystemTypeId(id)
  }
  useEffect(() => {
    if (isEmptyGuid(assetTypeId)) {
      let findItem = assetTypes.find((item) => item.id === systemTypeId)
      setAssetType((prev) => ({
        ...prev,
        type: findItem ? findItem.type : '',
        fieldConfiguration: findItem ? findItem.fieldConfiguration : '',
      }))
    }
  }, [assetTypeId, systemTypeId, assetTypes, setAssetType])
  useEffect(() => {
    if (errorStates?.saveAssetType?.formErrors) {
      setFormErrors(errorStates.saveAssetType.formErrors)
    }
  }, [errorStates, setFormErrors])

  const resetErrorByFormKey = useCallback(
    (formKey) => {
      setFormErrors((prev) => {
        if (prev[formKey]) {
          delete prev[formKey]
        }
        return prev
      })
    },
    [setFormErrors],
  )
  const getSystemTypeDisplaname = (type) => {
    let findItem = assetTypes.find(
      (item) => item.type === type && item.isSystemType,
    )
    return findItem ? findItem.displayName : ''
  }
  const fieldIsDisabled = (name) => {
    let disabled = getMetaProperty(
      assetType.metaProperties,
      `/${name}`,
      'isReadOnly',
    )
    return !!disabled
  }
  return (
    <StyledDiv>
      <LanguageNameInput
        openLanguageModal={() => setOverlayNameIsVisible(true)}
        languageArray={assetType.name.values}
        onChange={updateTranslations('name')}
        label={t('name')}
        disabled={fieldIsDisabled('name')}
        isInvalid={formErrors && formErrors.name}
      />
      <LanguageNameInput
        openLanguageModal={() => setOverlayDescriptionIsVisible(true)}
        languageArray={assetType.description.values}
        onChange={updateTranslations('description')}
        label={t('description')}
        disabled={fieldIsDisabled('description')}
        isInvalid={formErrors && formErrors.description}
      />

      <Form.Group>
        <Form.Check
          type='checkbox'
          id={`isGrouped`}
          name='isGrouped'
          label={t('assetTypes:isGrouped')}
          checked={assetType.isGrouped}
          onChange={updateInputFields}
          disabled={fieldIsDisabled('isGrouped')}
          isInvalid={formErrors && formErrors.isGrouped}
        />
        <Form.Check
          type='checkbox'
          id={`enableOrderProcess`}
          name='enableOrderProcess'
          label={t('assetTypes:enableOrderProcess')}
          checked={assetType.enableOrderProcess}
          onChange={updateInputFields}
          disabled={fieldIsDisabled('enableOrderProcess')}
          isInvalid={formErrors && formErrors.enableOrderProcess}
        />
        <Form.Check
          type='checkbox'
          id={`enableInventoryProcess`}
          name='enableInventoryProcess'
          label={t('assetTypes:enableInventoryProcess')}
          checked={assetType.enableInventoryProcess}
          onChange={updateInputFields}
          disabled={fieldIsDisabled('enableInventoryProcess')}
          isInvalid={formErrors && formErrors.enableInventoryProcess}
        />
        <Form.Control.Feedback type='invalid'>
          {formErrors.isGrouped ||
            formErrors.enableOrderProcess ||
            formErrors.enableInventoryProcess}
        </Form.Control.Feedback>
      </Form.Group>
      {!hideSelectSystemType && (
        <Form.Group>
          <Form.Label column>{t('assetTypes:selectSytemType')}</Form.Label>
          <ArraySelect
            value={systemTypeId}
            options={systemTypes}
            noAutoSelect
            name='systemTypes'
            onChange={(e) => setSelectedSystemType(e.target.value)}
            defaultOptionIsSelectable={true}
            returnEvent
          />
        </Form.Group>
      )}
      {(assetTypeId || hideSelectSystemType) && (
        <div className='my-3'>
          <Form.Label>{getSystemTypeDisplaname(assetType.type)}</Form.Label>
        </div>
      )}
      <div className='my-3'>
        {assetType.type === 'default' && (
          <AssetTypeDefaultForm
            configuration={assetType.fieldConfiguration}
            handleUpdateConfiguration={updateConfiguration}
          />
        )}
      </div>
      <ButtonWithLoadingState
        buttonText={t('saveButton')}
        isLoading={loadingStates.saveAssetType}
        onClick={handleSubmit}
      />
      <OverlayContainer>
        <OverlayRight
          visible={overlayNameIsVisible}
          onClose={handleCloseOverlay}
        >
          <LanguageForm
            languageTitle={t('name')}
            handleCloseOverlay={handleCloseOverlay}
            languageArray={assetType.name.values}
            onChange={updateTranslations('name')}
          />
        </OverlayRight>
        <OverlayRight
          visible={overlayDescriptionIsVisible}
          onClose={handleCloseOverlay}
        >
          <LanguageForm
            languageTitle={t('description')}
            handleCloseOverlay={handleCloseOverlay}
            languageArray={assetType.description.values}
            onChange={updateTranslations('description')}
          />
        </OverlayRight>
      </OverlayContainer>
      {successStates.saveAssetType && (
        <Alert variant='success' className='my-2'>
          {t('registrationBox:savingSuccess')}
        </Alert>
      )}
      {errorStates.saveAssetType && (
        <Alert variant='danger'>
          {t('articleAdminOverlayProgress:danger')}
        </Alert>
      )}
    </StyledDiv>
  )
}

export default AssetTypeGeneralForm
